import { useState } from 'react';
import { auth } from '@/firebaseConfig';
import { signInWithEmailAndPassword } from 'firebase/auth';
import {
  TextInput,
  Alert,
  Button,
  Title,
  PasswordInput,
  Stack,
} from '@mantine/core';
import { AlertOctagonIcon, LockIcon, MailIcon } from 'lucide-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import AuthTermsDisclaimer from './AuthTermsDisclaimer';
import { findEmployeeTenant } from '@/cloudFunctions/auth';

type FormState = { email: string; password: string } & (
  | { status: 'idle' }
  | { status: 'signingIn' }
  | { status: 'error'; message: string }
);

export const EmailPasswordLoginForm = () => {
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';
  const [state, setState] = useState<FormState>({
    status: 'idle',
    email,
    password: '',
  });
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (state.status === 'signingIn') return;

    setState({
      status: 'signingIn',
      email: state.email,
      password: state.password,
    });

    const formData = new FormData(e.currentTarget);
    const email = formData.get('email') as string;
    const password = formData.get('password') as string;

    if (!email || !password) return;

    let tenantId: string | undefined;
    try {
      tenantId = await findEmployeeTenant(email);
    } catch (error) {
      setState({
        status: 'error',
        message: 'Authentication error. Please try again.',
        email,
        password,
      });
      return;
    }
    if (!tenantId) {
      setState({
        status: 'error',
        message: 'Authentication error. Please try again.',
        email,
        password,
      });
      return;
    }

    auth.tenantId = tenantId;

    try {
      await signInWithEmailAndPassword(auth, email, password);
      navigate(`/matters`);
    } catch (error) {
      console.error('Signin failed:', (error as Error).message);
      setState({
        status: 'error',
        message: 'Invalid email or password',
        email,
        password,
      });
    }
  };

  const isLoading = state.status === 'signingIn';
  const errorMessage = state.status === 'error' ? state.message : '';

  return (
    <Stack>
      <Title order={1}>Sign in</Title>
      <form onSubmit={handleSubmit}>
        <Stack>
          <TextInput
            type='email'
            name='email'
            placeholder='you@example.com'
            value={state.email}
            onChange={(e) => setState({ ...state, email: e.target.value })}
            label='Email'
            leftSection={<MailIcon size={16} />}
            size='md'
            radius='md'
            required
          />
          <PasswordInput
            name='password'
            placeholder='Your password'
            label='Password'
            leftSection={<LockIcon size={16} />}
            size='md'
            radius='md'
            required
          />
          <Link to={`/forgot-password?email=${state.email}`}>
            <Button variant='subtle' size='compact-sm'>
              Forgot Password?
            </Button>
          </Link>
          {errorMessage && (
            <Alert
              color='red'
              radius='md'
              icon={<AlertOctagonIcon size={16} />}
            >
              {errorMessage}
            </Alert>
          )}
          <Button
            type='submit'
            loading={isLoading}
            disabled={isLoading}
            fullWidth
          >
            Sign In with Email
          </Button>
        </Stack>
      </form>
      <AuthTermsDisclaimer />
    </Stack>
  );
};

export default EmailPasswordLoginForm;
